/* Le document envoyé à l'usine ou au client.
 *
 * Chaque usine reçoit le format qu'elle attend : sa langue, ses intitulés de
 * colonnes, son incoterm, son unité. Le client, lui, reçoit un devis. Ce
 * fichier porte le vocabulaire ; le modèle porte les choix. */

import type { ModeleDocument } from "@/types/metier";

export interface Langue {
  cle: string;
  nom: string;
  date: string;
  total: string;
  lignes: string;
  incoterm: string;
  emis: string;
}

export const LANGUES: readonly Langue[] = [
  { cle: "fr", nom: "Français", date: "Date", total: "Total HT", lignes: "lignes",
    incoterm: "Incoterm", emis: "Émis par" },
  { cle: "en", nom: "English", date: "Date", total: "Total excl. VAT", lignes: "lines",
    incoterm: "Incoterm", emis: "Issued by" },
  { cle: "es", nom: "Español", date: "Fecha", total: "Total sin IVA", lignes: "líneas",
    incoterm: "Incoterm", emis: "Emitido por" },
  { cle: "it", nom: "Italiano", date: "Data", total: "Totale imponibile", lignes: "righe",
    incoterm: "Resa", emis: "Emesso da" },
];

export function langueDe(cle: string | null | undefined): Langue {
  return LANGUES.find((l) => l.cle === cle) ?? (LANGUES[0] as Langue);
}

export type CleColonne = "ref" | "nom" | "qte" | "unite" | "prix" | "total";

export interface ColonneDoc {
  cle: CleColonne;
  libelle: string;
  visible: boolean;
}

/** Les colonnes possibles, dans l'ordre où elles se lisent. */
export const COLONNES_DOC: { cle: CleColonne; defaut: string; chiffre: boolean }[] = [
  { cle: "ref", defaut: "Référence", chiffre: false },
  { cle: "nom", defaut: "Désignation", chiffre: false },
  { cle: "qte", defaut: "Quantité", chiffre: true },
  { cle: "unite", defaut: "Unité", chiffre: false },
  { cle: "prix", defaut: "Prix unitaire", chiffre: true },
  { cle: "total", defaut: "Montant", chiffre: true },
];

/** Un modèle neuf : toutes les colonnes, les prix seulement pour le client. */
export function colonnesParDefaut(avecPrix: boolean): ColonneDoc[] {
  return COLONNES_DOC.map((c) => ({
    cle: c.cle,
    libelle: c.defaut,
    visible: c.cle === "prix" || c.cle === "total" ? avecPrix : true,
  }));
}

/** Lit les colonnes d'un modèle en comblant ce qui manque. */
export function colonnesDe(m: Pick<ModeleDocument, "colonnes" | "cible">): ColonneDoc[] {
  const brut = Array.isArray(m.colonnes) ? (m.colonnes as unknown as ColonneDoc[]) : [];
  const defaut = colonnesParDefaut(m.cible === "client");
  if (!brut.length) return defaut;
  return defaut.map((d) => {
    const trouve = brut.find((c) => c && c.cle === d.cle);
    return trouve ? { cle: d.cle, libelle: trouve.libelle || d.libelle, visible: Boolean(trouve.visible) } : d;
  });
}

/** Le document porte-t-il des montants ? */
export function porteDesPrix(cols: readonly ColonneDoc[]): boolean {
  return cols.some((c) => c.visible && (c.cle === "prix" || c.cle === "total"));
}
